import { useState, useCallback, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';

// Promise-based confirm dialog — a drop-in, theme-aware replacement for window.confirm.
//
// Usage:
//   const [confirm, confirmModal] = useConfirm();
//   ...
//   if (await confirm({ message: 'Delete this?', confirmLabel: 'Delete' })) doIt();
//   ...
//   return (<>{/* page */}{confirmModal}</>);
//
// confirm(options) resolves true when the user confirms, false on cancel / backdrop /
// Escape. Options: { title, message, confirmLabel, cancelLabel, destructive (default true) }.
function ConfirmModal({ open, title, message, confirmLabel, cancelLabel, destructive, onConfirm, onCancel }) {
  return (
    <Modal isOpen={open} onClose={onCancel} title={title} maxWidth="max-w-sm">
      <p className="text-sm text-secondary/80 whitespace-pre-line">{message}</p>
      <div className="flex gap-2 justify-end mt-6">
        <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
        <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Button>
      </div>
    </Modal>
  );
}

export function useConfirm() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({
      title:        opts.title        || 'Are you sure?',
      message:      opts.message      || '',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel:  opts.cancelLabel  || 'Cancel',
      destructive:  opts.destructive ?? true,
    });
  }), []);

  const settle = useCallback((result) => {
    if (resolver.current) { resolver.current(result); resolver.current = null; }
    setState(null);
  }, []);

  const element = (
    <ConfirmModal
      open={!!state}
      title={state?.title}
      message={state?.message}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      destructive={state?.destructive}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return [confirm, element];
}

export default ConfirmModal;
